import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Shield, Plus, Calendar, AlertTriangle, Building2, Phone, Mail,
  Loader2, CheckCircle, Edit2, Euro
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { formatCurrency, formatDate } from "../../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const INSURANCE_TYPES = [
  { value: "all_risk", label: "Tous risques" },
  { value: "third_party", label: "Tiers" },
  { value: "intermediate", label: "Intermédiaire" },
];

export default function InsuranceTab({ vehicleId }) {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [activeInsurance, setActiveInsurance] = useState(null);
  const [renewalAlert, setRenewalAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company: "",
    contract_number: "",
    insurance_type: "all_risk",
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    annual_premium_ht: "",
    annual_premium_ttc: "",
    deductible: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
  });
  
  const token = localStorage.getItem("token");

  const fetchInsurance = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fleet/vehicles/${vehicleId}/insurance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(response.data.records || []);
      setActiveInsurance(response.data.active_insurance);
      setRenewalAlert(response.data.renewal_alert);
    } catch (err) {
      console.error("Error fetching insurance:", err);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [vehicleId, token]);

  useEffect(() => {
    fetchInsurance();
  }, [fetchInsurance]);

  const resetForm = () => {
    setForm({
      company: "",
      contract_number: "",
      insurance_type: "all_risk",
      start_date: new Date().toISOString().split("T")[0],
      end_date: "",
      annual_premium_ht: "",
      annual_premium_ttc: "",
      deductible: "",
      contact_name: "",
      contact_phone: "",
      contact_email: "",
    });
    setEditingId(null);
  };

  const handleEdit = (record) => {
    setForm({
      company: record.company || "",
      contract_number: record.contract_number || "",
      insurance_type: record.insurance_type || "all_risk",
      start_date: record.start_date || "",
      end_date: record.end_date || "",
      annual_premium_ht: record.annual_premium_ht || "",
      annual_premium_ttc: record.annual_premium_ttc || "",
      deductible: record.deductible || "",
      contact_name: record.contact_name || "",
      contact_phone: record.contact_phone || "",
      contact_email: record.contact_email || "",
    });
    setEditingId(record.id);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.company || !form.start_date || !form.end_date) {
      toast.error("Compagnie et dates requises");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        annual_premium_ht: form.annual_premium_ht ? parseFloat(form.annual_premium_ht) : null,
        annual_premium_ttc: form.annual_premium_ttc ? parseFloat(form.annual_premium_ttc) : null,
        deductible: form.deductible ? parseFloat(form.deductible) : null,
      };
      
      if (editingId) {
        await axios.put(
          `${API}/fleet/vehicles/${vehicleId}/insurance/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Assurance mise à jour");
      } else {
        await axios.post(
          `${API}/fleet/vehicles/${vehicleId}/insurance`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Assurance ajoutée");
      }
      
      setShowModal(false);
      resetForm();
      fetchInsurance();
    } catch (err) {
      console.error("Error saving insurance:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Assurance</h3>
          <p className="text-sm text-slate-500">
            Contrats et renouvellements
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="add-insurance-btn">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une assurance
        </Button>
      </div>

      {/* Renewal Alert */}
      {renewalAlert && (
        <Card className={renewalAlert.priority === "critical" ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-5 h-5 ${renewalAlert.priority === "critical" ? "text-red-600" : "text-amber-600"}`} />
              <div>
                <p className={`font-medium ${renewalAlert.priority === "critical" ? "text-red-800" : "text-amber-800"}`}>
                  Renouvellement à prévoir
                </p>
                <p className={`text-sm ${renewalAlert.priority === "critical" ? "text-red-700" : "text-amber-700"}`}>
                  L'assurance {renewalAlert.company} expire le {formatDate(renewalAlert.due_date)} (J-{renewalAlert.days_remaining})
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Insurance */}
      {activeInsurance ? (
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Assurance en cours
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => handleEdit(activeInsurance)}>
                <Edit2 className="w-4 h-4 mr-1" />
                Modifier
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700">Compagnie</p>
                    <p className="font-semibold text-green-900">{activeInsurance.company}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                    <Shield className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700">Type de couverture</p>
                    <p className="font-semibold text-green-900">{activeInsurance.insurance_type_label}</p>
                  </div>
                </div>
                
                {activeInsurance.contract_number && (
                  <div>
                    <p className="text-sm text-green-700">N° contrat</p>
                    <p className="font-mono text-green-900">{activeInsurance.contract_number}</p>
                  </div>
                )}
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700">Période</p>
                    <p className="font-semibold text-green-900">
                      {formatDate(activeInsurance.start_date)} → {formatDate(activeInsurance.end_date)}
                    </p>
                    {activeInsurance.days_until_renewal && (
                      <Badge variant="outline" className="mt-1 text-green-700 border-green-300">
                        {activeInsurance.days_until_renewal} jours restants
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {activeInsurance.annual_premium_ttc && (
                    <div>
                      <p className="text-sm text-green-700">Prime annuelle TTC</p>
                      <p className="font-semibold text-green-900">
                        {formatCurrency(activeInsurance.annual_premium_ttc)}
                      </p>
                    </div>
                  )}
                  {activeInsurance.deductible && (
                    <div>
                      <p className="text-sm text-green-700">Franchise</p>
                      <p className="font-semibold text-green-900">
                        {formatCurrency(activeInsurance.deductible)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Contact info */}
            {(activeInsurance.contact_name || activeInsurance.contact_phone || activeInsurance.contact_email) && (
              <div className="mt-4 pt-4 border-t border-green-200">
                <p className="text-sm font-medium text-green-800 mb-2">Contact</p>
                <div className="flex flex-wrap gap-4">
                  {activeInsurance.contact_name && (
                    <span className="text-sm text-green-700">{activeInsurance.contact_name}</span>
                  )}
                  {activeInsurance.contact_phone && (
                    <span className="text-sm text-green-700 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {activeInsurance.contact_phone}
                    </span>
                  )}
                  {activeInsurance.contact_email && (
                    <span className="text-sm text-green-700 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {activeInsurance.contact_email}
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-8 text-center">
            <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucune assurance active</p>
            <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setShowModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une assurance
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Historical records */}
      {records.filter(r => !r.is_active).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Historique</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {records.filter(r => !r.is_active).map((record) => (
                <div key={record.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div>
                    <p className="font-medium text-slate-900">{record.company}</p>
                    <p className="text-sm text-slate-500">
                      {formatDate(record.start_date)} → {formatDate(record.end_date)}
                    </p>
                  </div>
                  <Badge variant="outline">{record.insurance_type_label}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifier l'assurance" : "Ajouter une assurance"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Compagnie *</Label>
                <Input
                  placeholder="Ex: AXA"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>N° contrat</Label>
                <Input
                  placeholder="Ex: 123456789"
                  value={form.contract_number}
                  onChange={(e) => setForm({ ...form, contract_number: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Type de couverture *</Label>
              <Select
                value={form.insurance_type}
                onValueChange={(v) => setForm({ ...form, insurance_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INSURANCE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date début *</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Date fin *</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Prime HT</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.annual_premium_ht}
                  onChange={(e) => setForm({ ...form, annual_premium_ht: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Prime TTC</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.annual_premium_ttc}
                  onChange={(e) => setForm({ ...form, annual_premium_ttc: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Franchise</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.deductible}
                  onChange={(e) => setForm({ ...form, deductible: e.target.value })}
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Contact assureur</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input
                    placeholder="Nom"
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    placeholder="01 23 45 67 89"
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="contact@..."
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : editingId ? (
                <Edit2 className="w-4 h-4 mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              {editingId ? "Mettre à jour" : "Ajouter"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
